import {
    useRef
} from 'react';

import useListeningCurationFilterValues from './useListeningCurationFilterValues.js';
import useMetrics from '../../../hooks/useMetrics.js';

export const ValidationStates = {
    Validating: 'Validating',
    Valid: 'Valid',
    Errored: 'Errored',
    Invalid: 'Invalid'
};

// Because we want to show errors on the most recently edited fields, let's keep track of the order in which fields have been updated/created.
function updateOrderedEditList(previousOrderedEditList, metricFilters) {
    const allFilterPaths = [];
    _.each(['searchTerms', 'searchTermsNot', 'domain', 'excludeDomain', 'excludeAuthor'], (filterType) => {
        _.each(metricFilters.listening[filterType], (value, idx) => {
            const path = `${filterType}.${idx}`;
            allFilterPaths.push({
                path: path,
                id: `${path}.${value}`
            });
        });
    });
    _.each(metricFilters.listening.searchTermsAnd, (andGroup, groupIdx) => {
        _.each(andGroup, (andTerm, termIdx) => {
            const path = `searchTermsAnd.${groupIdx}.${termIdx}`;
            allFilterPaths.push({
                path: path,
                id: `${path}.${andTerm}`
            });
        });
    });


    const allQueryTerms = _.map(allFilterPaths, 'id');

    const orderedTerms = _.intersection(previousOrderedEditList, allQueryTerms).concat(_.difference(allQueryTerms, previousOrderedEditList));
    return orderedTerms;
}

export function useVerifySearch(shouldValidate, listeningFilterOverrides, validatesSearchTermsOnly = false) {
    const metricFilters = useListeningCurationFilterValues();

    const latestPathUpdatesRef = useRef([]);

    _.extend(metricFilters.listening, listeningFilterOverrides);

    let queryValidationErrors = RivalIQ.metrics.new.validation.validateListeningFilters(metricFilters.listening);

    const isQueryValid = !_.find(_.flattenDeep(_.values(queryValidationErrors)), {
        isFatal: true
    });

    const metricRequests = {};
    if (shouldValidate && isQueryValid && !_.isEmpty(metricFilters.listening.searchTerms)) {
        metricRequests.estimatedSearchMetric = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: 'estimatedCountBelowThreshold',
            filters: metricFilters
        };
    }

    const {isLoading, error, data: metricResults} = useMetrics(metricRequests, {
        requestGroup: 'verifySearch'
    });

    let validationState;
    let isQueryNarrowEnough;

    if (!isQueryValid) {
        validationState = ValidationStates.Invalid;
    } else {
        if (error) {
            validationState = ValidationStates.Errored;
        } else if (isLoading) {
            validationState = ValidationStates.Validating;
        }

        if (shouldValidate && metricResults?.estimatedSearchMetric?.data) {
            isQueryNarrowEnough = metricResults.estimatedSearchMetric.data[0][0];
            validationState = isQueryNarrowEnough ? ValidationStates.Valid : ValidationStates.Invalid;

            if (!isQueryNarrowEnough) {
                let message = 'This search is too broad. Please narrow your search by reducing the sources, adding Must Include or Must Exclude terms or changing the primary terms.';
                if (validatesSearchTermsOnly) {
                    message = 'This search is too broad. Please narrow your search by adding or changing the primary terms.';
                }

                queryValidationErrors = {
                    searchTerms: [{
                        group: 'searchTerms',
                        isFatal: true,
                        message: message
                    }]
                };
            }
        }
    }

    latestPathUpdatesRef.current = updateOrderedEditList(latestPathUpdatesRef.current, metricFilters);

    const pathToIdx = _.reduce(latestPathUpdatesRef.current, (memo, termId, idx) => {
        const termIdParts = termId.split('.');
        const path = _.take(termIdParts, termIdParts[0] === 'searchTermsAnd' ? 3 : 2).join('.');
        memo[path] = idx;
        return memo;
    }, {});

    const allErrors = _.flattenDeep(_.values(queryValidationErrors));

    let orderedErrors = _.sortBy(allErrors, error => pathToIdx[error.path]);
    const seenDuplicatePaths = {};

    // remove duplicate errors - if a field is in error because it's in, say, searchTerms and searchTermsNot, we want
    // to emit the error for the most recently edited pill.
    for (let errorIdx = orderedErrors.length - 1; errorIdx >= 0; errorIdx--) {
        const error = orderedErrors[errorIdx];
        if (error.otherOccurrencePath) {
            if (seenDuplicatePaths[error.otherOccurrencePath]) {
                delete orderedErrors[errorIdx];
            } else {
                seenDuplicatePaths[error.path] = error;
            }
        }
    }

    orderedErrors = _.compact(orderedErrors);
    return {
        validationState,
        queryValidationErrors,
        orderedErrors,
        isQueryNarrowEnough
    };
}
